import { PermissionChoices } from 'src/gen-types'

import { useMe } from './useMe'
import { useUserRolePermissions } from './useUserRolePermissions'

/**
 * The admin-definable "Contractor Foreman" role name (3.12.6). Kept here only
 * as a human-readable label / corroboration — the GATING signal below is
 * permission-driven, not name-driven, so a rename never silently widens a
 * foreman's access.
 */
export const CONTRACTOR_FOREMAN_ROLE_NAME = 'Contractor Foreman'

/**
 * The two platform-created contractor roles a contractor manager may put crew
 * on (PRO-30 "roles: manager/driver"; the BE invite choke point enforces the
 * same pair). Names mirror `core/services/contractor_role.py`, which the BE
 * itself keys on (its foreman write-ceiling is name-keyed too).
 */
export const CONTRACTOR_MANAGER_ROLE_NAME = 'Contractor Manager'
export const CONTRACTOR_DRIVER_ROLE_NAME = 'Contractor Driver'

/** All three contractor roles, for "is this a hauler-linked role" checks. */
export const CONTRACTOR_ROLE_NAMES = [
  CONTRACTOR_MANAGER_ROLE_NAME,
  CONTRACTOR_DRIVER_ROLE_NAME,
  CONTRACTOR_FOREMAN_ROLE_NAME,
]

/**
 * Distinguishes the contractor (hauler) sub-personas the platform now supports
 * (3.12.6). Every contractor user carries the coarse `hauler` role for routing;
 * this hook adds the finer manager-vs-foreman distinction the FE needs to hide
 * manager-only controls from a foreman.
 *
 * The distinction is PERMISSION-DRIVEN (verified against the live backend):
 *   - Contractor Manager holds fleet/crew management permissions
 *     (`driver__create`, `vehicle__edit`, `driver__edit`, …).
 *   - Contractor Foreman holds ONLY day-ops edits (`route__edit` +
 *     `pickup_order__edit`) — i.e. it holds NONE of the management permissions.
 *   - Contractor Driver holds none either (day-ops happens in the driver app).
 *
 * So "can manage the contractor" == holds any fleet/crew management permission.
 * This is rename-proof and mirrors how the rest of the app already gates write
 * controls (`useUserRolePermissions().has(...)`).
 */
export const useContractorPersona = () => {
  const me = useMe()
  const { has, ...rest } = useUserRolePermissions()

  // A hauler user is a contractor user. Staff never lose office access, so a
  // staff account is never treated as a contractor persona (mirrors setRoles).
  const isContractor = Boolean(me?.isHaulerUser && !me?.isStaff)

  // Manager-level == holds any fleet/crew management permission a foreman never
  // has. (Foreman perms are exactly route__edit + pickup_order__edit.)
  const canManageContractor =
    has(PermissionChoices.DRIVER_CREATE) ||
    has(PermissionChoices.VEHICLE_EDIT) ||
    has(PermissionChoices.DRIVER_EDIT)

  // Crew (user-management) capability, the exact strings the BE's contractor
  // user-management lane rides on (PRO-30, core/services/contractor_users.py):
  // invite/re-send = driver__create, edit = driver__edit, deactivate =
  // driver__delete. Foremen and drivers hold none of them, so the Users
  // screen is absent for them — the same accounts the server denies.
  const canManageCrew =
    has(PermissionChoices.DRIVER_CREATE) ||
    has(PermissionChoices.DRIVER_EDIT) ||
    has(PermissionChoices.DRIVER_DELETE)

  return {
    ...rest,
    has,
    roleName: me?.companyRole?.name ?? null,
    isContractor,
    canManageContractor,
    canManageCrew,
    // A foreman is a contractor user without management capability. Office /
    // manager users (canManageContractor === true) are never foremen.
    isContractorForeman: isContractor && !canManageContractor,
  }
}
